import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lightbulb, ThumbsUp, Send, Brain, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const STATUS_CONFIG = {
  nouvelle: { label: 'Nouvelle', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  en_cours: { label: 'En cours', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  approuvee: { label: 'Approuvee', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  refusee: { label: 'Refusee', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
};

const CATEGORIES = [
  { value: 'amelioration', label: 'Amelioration' },
  { value: 'investissement', label: 'Investissement' },
  { value: 'organisation', label: 'Organisation' },
  { value: 'securite', label: 'Securite' },
  { value: 'autre', label: 'Autre' },
];

const EmployeeIdeas = ({ auth, onLogout }) => {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'amelioration' });
  const [submitting, setSubmitting] = useState(false);

  const loadIdeas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/insights/ideas`);
      setIdeas(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const submitIdea = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Titre et description requis');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/insights/ideas`, {
        employee_id: auth.employee.id,
        title: form.title,
        description: form.description,
        category: form.category,
      });
      toast.success('Idee soumise avec succes !');
      setShowForm(false);
      setForm({ title: '', description: '', category: 'amelioration' });
      loadIdeas();
    } catch { toast.error('Erreur lors de la soumission'); }
    finally { setSubmitting(false); }
  };

  const voteIdea = async (ideaId) => {
    try {
      await axios.post(`${API}/insights/ideas/${ideaId}/vote`);
      loadIdeas();
    } catch {}
  };

  const myName = `${auth.employee.first_name} ${auth.employee.last_name}`;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-white/10" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee/dashboard')} className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              <p className="text-white text-sm font-semibold">Boite a idees</p>
            </div>
          </div>
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid="new-idea-button">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle idee
          </Button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {/* Submission form */}
        {showForm && (
          <div className="rounded-2xl p-4 border border-amber-500/30" style={{ background: 'rgba(30, 41, 59, 0.9)' }} data-testid="idea-form">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-amber-400" />
              <p className="text-sm font-semibold text-white">Proposer une idee</p>
            </div>

            {/* Auto-filled employee name */}
            <div className="mb-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Auteur</p>
              <p className="text-sm text-white font-medium" data-testid="idea-author-name">{myName}</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Titre *</label>
                <Input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Meilleur rangement des outils"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                  data-testid="idea-title-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Description *</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Decrivez votre idee en detail..."
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg p-2.5 text-sm placeholder:text-slate-500 h-24 resize-none"
                  data-testid="idea-desc-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Categorie</label>
                <select
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 text-white rounded-lg p-2.5 text-sm"
                  data-testid="idea-category-select"
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setShowForm(false)} className="flex-1 text-xs text-slate-300 border-white/10 hover:bg-white/5">
                  Annuler
                </Button>
                <Button size="sm" onClick={submitIdea} disabled={submitting} className="flex-1 text-xs bg-amber-500 hover:bg-amber-600 text-white" data-testid="submit-idea-button">
                  <Send className="w-3.5 h-3.5 mr-1" /> {submitting ? 'Envoi...' : 'Soumettre'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Ideas list */}
        {loading ? (
          <p className="text-center text-slate-500 py-10">Chargement...</p>
        ) : ideas.length === 0 ? (
          <div className="text-center py-16">
            <Lightbulb className="w-12 h-12 mx-auto mb-3 text-amber-500/30" />
            <p className="text-slate-400 text-sm">Aucune idee pour le moment</p>
            <p className="text-slate-500 text-xs mt-1">Soyez le premier a proposer une idee !</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{ideas.length} idee{ideas.length > 1 ? 's' : ''}</p>
            {ideas.map(idea => {
              const st = STATUS_CONFIG[idea.status] || STATUS_CONFIG.nouvelle;
              const isMine = idea.employee_id === auth.employee.id;
              return (
                <div
                  key={idea.id}
                  className={`rounded-xl p-4 border ${isMine ? 'border-amber-500/20' : 'border-white/5'}`}
                  style={{ background: 'rgba(30, 41, 59, 0.7)' }}
                  data-testid={`idea-card-${idea.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-sm font-semibold text-white">{idea.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.text} border ${st.border}`}>
                          {st.label}
                        </span>
                        {isMine && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">Mon idee</span>}
                      </div>
                      <p className="text-xs text-slate-400 mb-2">{idea.description}</p>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500">
                        <span>{idea.employee_name}</span>
                        <span>{new Date(idea.created_at).toLocaleDateString('fr-CH')}</span>
                        <span className="capitalize">{idea.category}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => voteIdea(idea.id)}
                      className="text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 shrink-0"
                      data-testid={`vote-idea-${idea.id}`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5 mr-1" />
                      <span className="text-xs">{idea.votes || 0}</span>
                    </Button>
                  </div>

                  {/* AI Analysis if present */}
                  {idea.ai_analysis && (
                    <div className="mt-3 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <p className="text-[10px] font-semibold text-indigo-400 mb-1 flex items-center gap-1">
                        <Brain className="w-3 h-3" /> Analyse IA
                      </p>
                      <p className="text-xs text-slate-300 whitespace-pre-wrap">{idea.ai_analysis}</p>
                    </div>
                  )}

                  {/* Admin response if present */}
                  {idea.admin_response && (
                    <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <p className="text-[10px] font-semibold text-emerald-400 mb-0.5">Reponse admin</p>
                      <p className="text-xs text-slate-300">{idea.admin_response}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeIdeas;
